import React from 'react';
import PptxGenJS from 'pptxgenjs';
import firstSlideLogo from '../assets/First Slide Logo 2.png'; 
import firstSlideShield from '../assets/First slide shield.png';

const DemographicsPPTX = ({ genderData, raceData, ageData, workingStatusData, employmentStatusData }) => {

  // Function to create a slide with demographic data
  const createSlide = (pptx, title, data, chartType, categories) => {
    if (!data || !Array.isArray(data)) {
      console.error("Invalid data provided for slide:", title);
      return;
    }

    const slide = pptx.addSlide();
    const years = data.map(item => item.year || item.Year); 
    const chartData = categories.map(category => ({
      name: category,
      labels: years,
      values: data.map(item => item[category.toLowerCase()] || item[category]), 
      color: categoryColors[category]
    }));

    slide.addText(title, {
      x: 'center', y: 0.8, fontSize: 24, color: '#4682b4', bold: true, align: 'center', fontFace: 'Segoe UI'
    });

    slide.addChart(chartType, chartData, {
      x: 1, y: 1, w: 8, h: 4,
      showLegend: true,
      legendPos: 'b',
      legendColor: '000000',
      legendFontSize: 14,
      barGrouping: 'stacked',
      title: title,
      titleFontSize: 28,
      titleAlign: 'center',
      showValue: true,
      dataLabelFontFace: 'Verdana',
      dataLabelFontSize: 12,
      dataLabelColor: '#FFFFFF',
      dataLabelPosition: 'ctr',
      dataLabelPlacement: 't',
      majorGridline: false,
      valAxisHidden: true,
      catAxisHidden: false,
      valGridLine: { style: "none" },
      chartColors: categories.map(category => categoryColors[category])
    });
  };

  const categoryColors = {
    Male: '#5D8AA8',
    Female: '#FF69B4',   
    Black: '#7CB9E8',
    Coloured: '#800080',
    Indian: '#FFA500',
    White: '#FF69B4',
    '16-24': '#FF6347',
    '25-34': '#4682B4',
    '35-44': '#32CD32',
    '45-54': '#FFD700',
    '55-69': '#FF69B4',
    '70+': '#8A2BE2',
    'Work(Full-time)': '#0000FF',
    'Work(Part-time)': '#FF69B4',
    'Student(Full-time)': '#FFD700',
    'Student(Part-time)': '#008000',
    'Scholar': '#FF4500',
    'Pensioner': '#8A2BE2',
    'Unemployed': '#D2691E',
    'Housewife/Househusband': '#FF7F50',
    'Employed by company': '#4682B4',
    'Self-employed': '#32CD32',
    'Employed by a person': '#FFD700'
  };

  const generatePowerPoint = () => {
    const pptx = new PptxGenJS();

    // Slide 1 - Title Slide
    const slide1 = pptx.addSlide();
    slide1.addShape(pptx.ShapeType.rect, {
      x: 0, y: 0, w: '100%', h: '100%',
      fill: { color: '#0039a6' }
    });
    slide1.addImage({ path: firstSlideLogo, x: 0.4, y: 0.3, w: 4, h: 1.2 });
    slide1.addText('DEMOGRAPHICS', { x: 0.5, y: 2.5, fontSize: 35, color: '#FFFFFF' });
    slide1.addText('PERSONAL &', { x: 0.5, y: 3, fontSize: 35, color: '#ADD8E6' });
    slide1.addText('PRIVATE BANKING (PPB)', { x: 0.5, y: 3.5, fontSize: 35, color: '#ADD8E6' });
    slide1.addText('2017 - 2024 Results', { x: 0.5, y: 4.5, fontSize: 30, color: '#FFFFFF' });     
    slide1.addImage({ path: firstSlideShield, x: 6.5, y: 0.7, w: 3, h: 4 });

    // Slide 2 - Gender Demographics
    if (genderData) {     
      createSlide(pptx, 'Gender Demographics', genderData, pptx.ChartType.bar, ['Male', 'Female']);
    }

    // Slide 3 - Race Demographics
    if (raceData) {     
      createSlide(pptx, 'Race Demographics', raceData, pptx.ChartType.bar, ['Black', 'Coloured', 'Indian', 'White']);
    } 

    // Slide 4 - Age Demographics
    if (ageData) {    
      createSlide(pptx, 'Age Demographics', ageData, pptx.ChartType.bar, ['16-24', '25-34', '35-44', '45-54', '55-69', '70+']);
    } 

    // Slide 5 - Working Status
    if (workingStatusData) {
      createSlide(pptx, 'Working Status', workingStatusData, pptx.ChartType.bar, [
        'Work(Full-time)', 'Work(Part-time)', 'Student(Full-time)', 'Scholar', 'Pensioner', 'Unemployed', 'Student(Part-time)', 'Housewife/Househusband'
      ]);
    }

    // Additional slides for each year
    if (employmentStatusData) {
      employmentStatusData.forEach(yearData => {
        const slide = pptx.addSlide();
        slide.addText(`Employment Status - ${yearData.year}`, {
          x: 'center', y: 0.8, fontSize: 24, color: '#4682b4', bold: true, align: 'center', fontFace: 'Segoe UI'
        });

        const chartData = [];
        Object.keys(yearData.status_data[0].bank_percentages).forEach((bank, index) => {
          const bankData = {
            name: bank,
            labels: yearData.status_data.map(status => status.label),
            values: yearData.status_data.map(status => status.bank_percentages[bank]),
            color: ['#C8102E', '#E75480', '#003876', '#005941', '#0473EA', '#f4ca16'][index % 6] 
          };
          chartData.push(bankData);
        });

        slide.addChart(pptx.ChartType.bar, chartData, {
          x: 1, y: 1, w: 8, h: 4,
          showLegend: true,
          legendPos: 'b',
          legendColor: '000000',
          legendFontSize: 14,
          barGrouping: 'stacked',
          title: `Employment Status - ${yearData.year}`,
          titleFontSize: 28,
          titleAlign: 'center',
          showValue: true,
          dataLabelFontFace: 'Verdana',
          dataLabelFontSize: 12,
          dataLabelColor: '#FFFFFF',
          dataLabelPosition: 'ctr',
          dataLabelPlacement: 't',
          majorGridline: false,
          valAxisHidden: true,
          catAxisHidden: false,
          valGridLine: { style: "none" },
          chartColors: ['#C8102E', '#E75480', '#003876', '#005941', '#0473EA', '#f4ca16'] 
        });
      });
    }

    pptx.writeFile('Demographics Presentation');
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '2vh auto', width: '50%' }}>
      <button onClick={generatePowerPoint}>Download PowerPoint</button>
    </div>
  );
};

export default DemographicsPPTX;
